import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

import { AuthLayout, AuthLink } from "@/components/auth/AuthLayout";
import { authService } from "@/services/authService";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AUTH } from "@/constants/testIds";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { refresh, status } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    authService
      .verifyEmail(token)
      .then(async () => {
        setState("success");
        setMessage("Your email has been verified.");
        await refresh();
      })
      .catch((err) => {
        setState("error");
        setMessage(err.message || "This verification link is invalid or has expired.");
      });
  }, [token, refresh]);

  return (
    <AuthLayout title="Email verification" subtitle="Confirming your email address.">
      <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid={AUTH.verifyPage}>
        {state === "verifying" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-accent" />
            <p className="text-sm text-muted-foreground">Verifying…</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-success" />
            <p className="font-medium text-foreground">{message}</p>
            <Button asChild className="mt-2 w-full">
              <Link to="/">{status === "authenticated" ? "Continue to AgentHQ" : "Go to sign in"}</Link>
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="h-12 w-12 text-destructive" />
            <p className="font-medium text-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">You can request a new link after signing in.</p>
            <div className="mt-2 text-sm"><AuthLink to="/login">Back to sign in</AuthLink></div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
